import React, { useState, useEffect } from 'react';
import axios from 'axios';
import lcaClient from '../../services/lcaClient';
const api = lcaClient;
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { toast } from 'sonner';
import {
  Lock, Unlock, Save, AlertTriangle, ChevronDown, Sparkles, Target,
  Globe, Users, FileCheck, Info, Package
} from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const studyGoalOptions = [
  { value: 'internal_improvement', label: 'Internal Improvement / Eco-design', description: 'Identify hotspots for product improvement' },
  { value: 'customer_disclosure', label: 'Customer Disclosure', description: 'Share environmental performance with customers' },
  { value: 'regulatory', label: 'Regulatory / Compliance', description: 'Meet regulatory requirements (EPD, CBAM)' },
  { value: 'comparative_assertion', label: 'Comparative Assertion', description: 'Compare products (requires stricter governance)', warning: true }
];

const systemBoundaryOptions = [
  { value: 'cradle_to_gate', label: 'Cradle-to-Gate', description: 'Raw materials to factory gate' },
  { value: 'cradle_to_grave', label: 'Cradle-to-Grave', description: 'Full lifecycle including end-of-life' },
  { value: 'gate_to_gate', label: 'Gate-to-Gate', description: 'Manufacturing processes only' }
];

const boundaryInclusionOptions = [
  { id: 'capital_goods', label: 'Capital Goods' },
  { id: 'packaging', label: 'Packaging' },
  { id: 'transport_to_customer', label: 'Transport to Customer' },
  { id: 'end_of_life', label: 'End-of-Life Treatment' }
];

const audienceOptions = [
  { id: 'internal_technical', label: 'Internal Technical Team' },
  { id: 'management', label: 'Management' },
  { id: 'external_customers', label: 'External Customers' },
  { id: 'regulators', label: 'Regulators / Auditors' }
];

const geographyOptions = ['Global', 'India', 'EU', 'US', 'China', 'Japan', 'Germany', 'UK', 'Other'];

export default function Phase1GoalScope({ project, onUpdate, isLocked }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  
  const [formData, setFormData] = useState({
    study_goal: '',
    functional_unit_value: '',
    functional_unit_unit: '',
    functional_unit_description: '',
    system_boundary: '',
    boundary_inclusions: [],
    primary_geography: '',
    secondary_geography: [],
    intended_audience: [],
    allocation_approach: '',
    cut_off_criteria: '',
    known_exclusions: '',
    reference_standards: []
  });
  
  const [goalScope, setGoalScope] = useState(null);

  // Linked product master row (resolved from project.master_product_id).
  // Surfaces as context in the header and seeds Functional Unit defaults.
  const [linkedProduct, setLinkedProduct] = useState(null);

  useEffect(() => {
    fetchGoalScope();
  }, [project.id]);

  // Look up the linked product master entry whenever the project's
  // master_product_id changes. Cheap: filtered server-side.
  useEffect(() => {
    if (!project?.master_product_id) {
      setLinkedProduct(null);
      return;
    }
    const token = localStorage.getItem('token');
    axios
      .get(`${BACKEND_URL}/api/products/${project.master_product_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setLinkedProduct(r.data?.product || null))
      .catch(() => setLinkedProduct(null));
  }, [project?.master_product_id]);

  // Smart prefill: when a product is linked AND the Functional Unit fields
  // are still empty, seed value=1, unit=product.unit, description="1 <unit>
  // of <product name>". The practitioner refines for ISO compliance (purity,
  // service-life, etc.) — they don't have to type the boilerplate.
  useEffect(() => {
    if (!linkedProduct || loading || isLocked) return;
    setFormData((prev) => {
      const fuEmpty =
        !prev.functional_unit_value &&
        !prev.functional_unit_unit &&
        !prev.functional_unit_description;
      if (!fuEmpty) return prev;
      const unit = linkedProduct.unit || 'unit';
      return {
        ...prev,
        functional_unit_value: 1,
        functional_unit_unit: unit,
        functional_unit_description: `1 ${unit} of ${linkedProduct.name}`,
      };
    });
  }, [linkedProduct, loading, isLocked]);

  const fetchGoalScope = async () => {
    try {
      const response = await api.get(`/projects/${project.id}/goal-scope`);
      setGoalScope(response.data);
      setFormData({
        study_goal: response.data.study_goal || '',
        functional_unit_value: response.data.functional_unit_value || '',
        functional_unit_unit: response.data.functional_unit_unit || '',
        functional_unit_description: response.data.functional_unit_description || '',
        system_boundary: response.data.system_boundary || '',
        boundary_inclusions: response.data.boundary_inclusions || [],
        primary_geography: response.data.primary_geography || '',
        secondary_geography: response.data.secondary_geography || [],
        intended_audience: response.data.intended_audience || [],
        allocation_approach: response.data.allocation_approach || '',
        cut_off_criteria: response.data.cut_off_criteria || '',
        known_exclusions: response.data.known_exclusions || '',
        reference_standards: response.data.reference_standards || []
      });
    } catch (error) {
      // No existing data
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.study_goal || !formData.functional_unit_value || !formData.system_boundary || 
        !formData.primary_geography || formData.intended_audience.length === 0) {
      toast.error('Please fill in all mandatory fields');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post(`/projects/${project.id}/goal-scope`, {
        ...formData,
        functional_unit_value: parseFloat(formData.functional_unit_value)
      });
      setGoalScope(response.data);
      toast.success('Goal & Scope saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLock = async () => {
    if (!goalScope) {
      toast.error('Please save Goal & Scope first');
      return;
    }

    setLocking(true);
    try {
      const response = await api.post(`/projects/${project.id}/goal-scope/lock`);
      toast.success('Phase 1 locked. AI summary generated.');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lock phase');
    } finally {
      setLocking(false);
    }
  };

  const handleUnlock = async () => {
    if (!window.confirm('Unlocking Phase 1 will reset all subsequent phases (Inventory, LCIA, Interpretation) to draft status. LCIA results and AI insights will be deleted. Do you want to continue?')) {
      return;
    }

    setUnlocking(true);
    try {
      await api.post(`/projects/${project.id}/goal-scope/unlock`);
      toast.success('Phase 1 unlocked. You can now edit Goal & Scope.');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock phase');
    } finally {
      setUnlocking(false);
    }
  };

  const toggleArrayItem = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value) 
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="phase1-goal-scope">
      {/* Main Form */}
      <div className="lg:col-span-2 space-y-6">
        {/* Phase Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${isLocked ? 'bg-secondary/20' : 'bg-primary/20'}`}>
              {isLocked ? <Lock className="h-6 w-6 text-secondary" /> : <Target className="h-6 w-6 text-primary" />}
            </div>
            <div>
              <h2 className="text-2xl font-bold">ISO Phase 1 – Goal & Scope Definition</h2>
              <p className="text-muted-foreground">Define why the LCA is conducted and how results will be used</p>
            </div>
          </div>
          <Badge variant={isLocked ? "secondary" : "outline"} className="text-sm">
            {isLocked ? 'Locked' : goalScope ? 'Draft' : 'Not Started'}
          </Badge>
        </div>

        {isLocked && goalScope?.ai_summary && (
          <Alert className="border-secondary/50 bg-secondary/10">
            <Sparkles className="h-4 w-4 text-secondary" />
            <AlertTitle>AI Summary (Read-Only)</AlertTitle>
            <AlertDescription className="whitespace-pre-wrap text-sm mt-2">
              {goalScope.ai_summary}
            </AlertDescription>
          </Alert>
        )}

        {/* Study Goal */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Study Goal *
            </CardTitle>
            <CardDescription>Define the purpose of this LCA study</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup 
              value={formData.study_goal} 
              onValueChange={(v) => setFormData({...formData, study_goal: v})}
              disabled={isLocked}
              className="space-y-3"
            >
              {studyGoalOptions.map((option) => (
                <div key={option.value} className={`flex items-start space-x-3 p-3 rounded-lg border ${
                  formData.study_goal === option.value ? 'border-primary bg-primary/5' : 'border-border'
                } ${option.warning ? 'border-accent/50' : ''}`}>
                  <RadioGroupItem value={option.value} id={option.value} data-testid={`study-goal-${option.value}`} />
                  <div className="flex-1">
                    <Label htmlFor={option.value} className="font-medium cursor-pointer flex items-center gap-2">
                      {option.label}
                      {option.warning && <AlertTriangle className="h-4 w-4 text-accent" />}
                    </Label>
                    <p className="text-sm text-muted-foreground">{option.description}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
            {formData.study_goal === 'comparative_assertion' && (
              <Alert className="mt-4 border-accent/50 bg-accent/10">
                <AlertTriangle className="h-4 w-4 text-accent" />
                <AlertTitle>Comparative Assertion Selected</AlertTitle>
                <AlertDescription>
                  This requires stricter governance and third-party verification per ISO 14044.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Linked Product (read-only context) — product master link is set
            on the project header; this card just surfaces it inside Phase 1
            so the FU author can see what's being assessed. */}
        {linkedProduct && (
          <Card className="glass-card border-teal-200 bg-teal-50/40">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="h-4 w-4 text-teal-700" />
                Linked Product
              </CardTitle>
              <CardDescription className="text-xs">
                The product this LCA assesses. Per ISO 14040 the functional unit (below)
                must still quantify what this product <em>delivers</em> — e.g. a duration,
                performance, or service. The product alone isn't an FU.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <div className="font-medium">{linkedProduct.name}</div>
                </div>
                {linkedProduct.code && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Code / SKU</Label>
                    <div className="font-medium">{linkedProduct.code}</div>
                  </div>
                )}
                {linkedProduct.unit && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Default unit</Label>
                    <div className="font-medium">{linkedProduct.unit}</div>
                  </div>
                )}
                {linkedProduct.hs_code && (
                  <div>
                    <Label className="text-xs text-muted-foreground">HS code</Label>
                    <div className="font-medium">{linkedProduct.hs_code}</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Functional Unit */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Functional Unit *</CardTitle>
            <CardDescription>
              Quantifiable reference for inputs/outputs.
              {linkedProduct && (
                <span className="block mt-1 text-xs text-teal-700">
                  Seeded from <strong>{linkedProduct.name}</strong>; refine with
                  performance criteria (purity, service-life, quality).
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Numeric Value</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g., 1"
                  value={formData.functional_unit_value}
                  onChange={(e) => setFormData({...formData, functional_unit_value: e.target.value})}
                  disabled={isLocked}
                  data-testid="functional-unit-value"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input
                  placeholder="e.g., solar module, kg, kWh"
                  value={formData.functional_unit_unit}
                  onChange={(e) => setFormData({...formData, functional_unit_unit: e.target.value})}
                  disabled={isLocked}
                  data-testid="functional-unit-unit"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="e.g., 1 solar module (540 Wp) delivered to site"
                value={formData.functional_unit_description}
                onChange={(e) => setFormData({...formData, functional_unit_description: e.target.value})}
                disabled={isLocked}
                rows={2}
                data-testid="functional-unit-desc"
              />
            </div>
          </CardContent>
        </Card>

        {/* System Boundary */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>System Boundary *</CardTitle>
            <CardDescription>Define what's included in the assessment</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <RadioGroup 
              value={formData.system_boundary} 
              onValueChange={(v) => setFormData({...formData, system_boundary: v})}
              disabled={isLocked}
              className="space-y-3"
            >
              {systemBoundaryOptions.map((option) => (
                <div key={option.value} className={`flex items-start space-x-3 p-3 rounded-lg border ${
                  formData.system_boundary === option.value ? 'border-primary bg-primary/5' : 'border-border'
                }`}>
                  <RadioGroupItem value={option.value} id={option.value} data-testid={`boundary-${option.value}`} />
                  <div>
                    <Label htmlFor={option.value} className="font-medium cursor-pointer">{option.label}</Label>
                    <p className="text-sm text-muted-foreground">{option.description}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>

            <div className="pt-4 border-t border-border">
              <Label className="mb-3 block">Optional Inclusions</Label>
              <div className="grid grid-cols-2 gap-3">
                {boundaryInclusionOptions.map((option) => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={option.id}
                      checked={formData.boundary_inclusions.includes(option.id)}
                      onCheckedChange={() => toggleArrayItem('boundary_inclusions', option.id)}
                      disabled={isLocked}
                      data-testid={`inclusion-${option.id}`}
                    />
                    <Label htmlFor={option.id} className="text-sm cursor-pointer">{option.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Geography */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              Geography *
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Primary Geography</Label>
              <Select 
                value={formData.primary_geography} 
                onValueChange={(v) => setFormData({...formData, primary_geography: v})}
                disabled={isLocked}
              >
                <SelectTrigger data-testid="primary-geography">
                  <SelectValue placeholder="Select primary geography" />
                </SelectTrigger>
                <SelectContent>
                  {geographyOptions.map(geo => (
                    <SelectItem key={geo} value={geo}>{geo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Intended Audience */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Intended Audience *
            </CardTitle>
            <CardDescription>Who will use these results?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {audienceOptions.map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={option.id}
                    checked={formData.intended_audience.includes(option.id)}
                    onCheckedChange={() => toggleArrayItem('intended_audience', option.id)}
                    disabled={isLocked}
                    data-testid={`audience-${option.id}`}
                  />
                  <Label htmlFor={option.id} className="text-sm cursor-pointer">{option.label}</Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Optional Fields */}
        <Collapsible open={showOptional} onOpenChange={setShowOptional}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between" data-testid="optional-fields-toggle">
              Optional: Assumptions & Exclusions
              <ChevronDown className={`h-4 w-4 transition-transform ${showOptional ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-4">
            <Card className="glass-card">
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <Label>Allocation Approach</Label>
                  <Textarea
                    placeholder="Describe how you will handle allocation..."
                    value={formData.allocation_approach}
                    onChange={(e) => setFormData({...formData, allocation_approach: e.target.value})}
                    disabled={isLocked}
                    rows={2}
                    data-testid="allocation-approach"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cut-off Criteria</Label>
                  <Textarea
                    placeholder="Specify mass, energy, or environmental relevance cut-offs..."
                    value={formData.cut_off_criteria}
                    onChange={(e) => setFormData({...formData, cut_off_criteria: e.target.value})}
                    disabled={isLocked}
                    rows={2}
                    data-testid="cutoff-criteria"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Known Exclusions</Label>
                  <Textarea
                    placeholder="List any intentional exclusions from the study..."
                    value={formData.known_exclusions}
                    onChange={(e) => setFormData({...formData, known_exclusions: e.target.value})}
                    disabled={isLocked}
                    rows={2}
                    data-testid="known-exclusions"
                  />
                </div>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Actions */}
        {!isLocked && (
          <div className="flex gap-4 justify-end pt-4">
            <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="save-goal-scope-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button onClick={handleLock} disabled={locking || !goalScope} data-testid="lock-phase1-btn">
              <Lock className="h-4 w-4 mr-2" />
              {locking ? 'Locking...' : 'Validate & Lock Phase 1'}
            </Button>
          </div>
        )}

        {/* Unlock Button when locked */}
        {isLocked && (
          <div className="flex gap-4 justify-end pt-4">
            <Button 
              variant="outline" 
              onClick={handleUnlock} 
              disabled={unlocking}
              className="border-amber-500 text-amber-600 hover:bg-amber-50"
              data-testid="unlock-phase1-btn"
            >
              <Unlock className="h-4 w-4 mr-2" />
              {unlocking ? 'Unlocking...' : 'Unlock & Edit Phase 1'}
            </Button>
          </div>
        )}
      </div>

      {/* AI Guidance Panel */}
      <div className="space-y-4">
        <Card className="glass-card sticky top-32">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Guidance – ISO Phase 1
            </CardTitle>
            <CardDescription>Educational assistance only</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {formData.study_goal === 'comparative_assertion' && (
              <Alert className="border-accent/50 bg-accent/10">
                <AlertTriangle className="h-4 w-4 text-accent" />
                <AlertDescription>
                  Comparative assertions require critical review per ISO 14044 Section 5.3
                </AlertDescription>
              </Alert>
            )}
            
            {formData.system_boundary === 'cradle_to_gate' && formData.intended_audience.includes('external_customers') && (
              <Alert className="border-warning/50 bg-warning/10">
                <Info className="h-4 w-4 text-warning" />
                <AlertDescription>
                  Cradle-to-gate may not be suitable for full product footprint disclosure to customers
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-3 pt-2">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium mb-1">Study Goal</p>
                <p className="text-muted-foreground text-xs">
                  Determines how results can be communicated and what governance applies.
                </p>
              </div>
              
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium mb-1">Functional Unit</p>
                <p className="text-muted-foreground text-xs">
                  Must be quantifiable and unambiguous. All inventory data will be normalized to this unit.
                </p>
              </div>
              
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium mb-1">System Boundary</p>
                <p className="text-muted-foreground text-xs">
                  Cradle-to-gate excludes use and end-of-life impacts. Cradle-to-grave is recommended for full product assessments.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground">
                AI assists with explanations only. It cannot change inputs, predict results, or recommend designs.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
